import { Flex, List, ListItem, Text } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Autocomplete,
  Avatar,
  Modal,
  ModalBody,
  ModalFooterDefault,
  ModalHeaderDefault,
  useSnackbar,
} from 'ui-components';
import { IconButton } from 'ui-components/dist/Button';
import { TrashIcon } from 'ui-components/dist/TrashIcon';
import { useCreateMediaKit } from '~/hooks/api/media-kits/create';
import { useSearchAgencyCreator } from '~/hooks/api/useSearchAgencyCreators';
import { selectTalentManager } from '~/selectors/talent-manager';
import { ICreator } from '~/types';

import { useSelectTalentStore } from '../store/useSelectTalentStore';
import { getMediaKitPayload } from '../utils';

export const SelectTalentModal: React.FC = () => {
  const { isModalOpen, reset } = useSelectTalentStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCreator, setSelectedCreator] = useState<
    ICreator | undefined
  >();
  const createMediaKit = useCreateMediaKit();
  const manager = useSelector(selectTalentManager);
  const agencyId = manager?.agency?.id;
  const { data: creators, isFetching } = useSearchAgencyCreator(
    searchQuery,
    agencyId,
  );
  const snackbar = useSnackbar();
  const router = useRouter();

  const options = useMemo(() => {
    if (!creators) return [];

    return creators.map((c: ICreator) => ({
      label: c.fullName,
      avatar: c.avatar,
      id: c, // Todo: Copied from src/screens/PageBuilder/sidebar/ModalSelectCreator.tsx. treat it the same way
    }));
  }, [creators]);

  const onCloseModal = () => {
    setSearchQuery(''); // Clear local state
    setSelectedCreator(undefined);
    reset();
  };
  const onAddClicked = async () => {
    if (selectedCreator) {
      try {
        const resp = await createMediaKit.mutateAsync(
          getMediaKitPayload(selectedCreator),
        );

        if (resp.data?.id) {
          router.push(`/media-kits/${resp.data.id}/builder`);
        }
        onCloseModal();
      } catch (error: any) {
        snackbar.error({
          description: 'Error—retry or contact hello@foam.io',
        });
      }
    }
  };

  return (
    <Modal isOpen={isModalOpen} onClose={onCloseModal}>
      <ModalHeaderDefault
        title="Search for Talent"
        subtitle="Select your Talent to generate their Media Kit"
      />
      <ModalBody>
        <Flex direction="column" gap={4}>
          <Autocomplete
            ariaLabel="Search creator"
            placeholder="Search by name or handle"
            onSelectOption={(opt) => {
              setSelectedCreator(opt.id as unknown as ICreator);
            }}
            onChange={setSearchQuery}
            options={options}
            isLoading={isFetching}
            trackId="media-kit-creator-input"
          />

          {selectedCreator && (
            <List spacing={2}>
              <ListItem key={selectedCreator.id}>
                <Flex
                  justify="space-between"
                  align="center"
                  p={2}
                  bg="gray.50"
                  borderRadius="md"
                >
                  <Flex gap={2} alignItems="center">
                    <Avatar
                      alt={selectedCreator.fullName as string}
                      name={selectedCreator.fullName}
                      size="sm"
                      src={selectedCreator.avatar}
                    />
                    <Text>{selectedCreator.fullName}</Text>
                  </Flex>
                  <IconButton
                    aria-label="Delete creator"
                    variant="tertiary"
                    icon={<TrashIcon />}
                    marginLeft="3"
                    onClick={() => setSelectedCreator(undefined)}
                    size="sm"
                  />
                </Flex>
              </ListItem>
            </List>
          )}
        </Flex>
      </ModalBody>
      <ModalFooterDefault
        primary={{
          title: 'Add',
          onClick: onAddClicked,
          isDisabled: !selectedCreator,
          isLoading: createMediaKit.isLoading,
          trackId: 'create-media-kit-save-btn',
        }}
        secondary={{
          onClick: onCloseModal,
          title: 'Cancel',
          trackId: 'create-media-kit-cancel-btn',
        }}
      />
    </Modal>
  );
};
